import { nextTick, reactive, watch } from 'vue';

export default class Seat {
  constructor(model, group, watcher) {
    this.id     = model.id;
    this.x      = model.pos[0];
    this.y      = model.pos[1];
    this.index  = model.index;
    this.category = model.category;
    this.available = model.available;
    this.model  = model;
    this.group  = group;
    this.key    = null;
    this.status = null;
    this.option = null;
    this._block = null;
    this._valid = null;
    this.anticipated = [];

    this.state = reactive({
      anticipate: null,
      accepted: null,
      selected: null,
      blocked: model.blocked,
      focus: null,
      valid: null,
    });

    if (watcher) {
      watch(() => this.state, () => watcher(this), { deep: true });
    }
  }

  get block() {
    if (this._block === null) this.group.setup()
    return this._block;
  }

  set block(value) {
    this._block = value;
  }

  get valid() {
    if (this._valid === null) this.setup();
    return this._valid;
  }

  set valid(value) {
    this._valid = value;
  }

  setup() {
    try {
      const { valid, option } = this.group.validator.validate(this);
      this.option = option;
      this.valid = valid;
    } catch (error) {
      this.option = null;
      this.valid = null;
    }
  }

  flush() {
    this.group.flush();

    this.block = null;
    this.valid = null;

    if (this.state.focus) {
      nextTick(() => {
        this.resetAnticpated();
        this.onFocus();
      });
    }
  }

  onClick() {
    if (this.valid) {
      this.group.toggle(this);
    }

    if (this.option) {
      this.option.deselect.forEach(seat => this.group.deselect(seat));
      this.option.select.forEach(seat => this.group.select(seat));
    }

    this.flush();

    // Required to make the validation work on mobile
    this.valid;
  }

  onFocus() {
    this.state.focus = true;
    this.state.valid = this.valid;

    if (this.option) {
      this.anticipate(this.option.deselect, 'deselect');
      this.anticipate(this.option.select, 'select');
    }
  }

  onBlur() {
    this.state.focus = false;
    this.state.valid = null;

    this.resetAnticpated();
  }

  anticipate(seats, anticipate) {
    seats.forEach(seat => {
      seat.state.anticipate = anticipate;
      this.anticipated.push(seat);
    });
  }

  resetAnticpated() {
    this.anticipated.forEach(seat => seat.state.anticipate = null);
    this.anticipated = [];
  }

  getStatus(delta) {
    return delta ? this.nth(delta)?.getStatus() : this.status;
  }

  exists(delta) {
    return (delta ? this.nth(delta)?.exists() : this.getStatus()) || false;
  }

  doesntExist(delta) {
    return !this.exists(delta)
  }

  get(key) {
    return this.block.seats[key] || null;
  }

  nth(delta = 0) {
    if (delta === 0) return this;
    return this.get(this.key + delta);
  }

  prev(delta = 1) {
    return this.nth(-delta);
  }

  next(delta = 1) {
    return this.nth(delta);
  }

  first() {
    return this.get(0);
  }

  last() {
    return this.get(this.block.seats.length - 1);
  }

  edge() {
    return Math.round(this.key / this.block.seats.length)
      ? this.last()
      : this.first()
  }

  isFirst(delta) {
    return this.nth(delta)?.id === this.first().id;
  }

  isNotFirst(delta) {
    return !this.isFirst(delta);
  }

  isLast(delta) {
    return this.nth(delta)?.id === this.last().id;
  }

  isNotLast(delta) {
    return !this.isLast(delta);
  }

  isEdge(delta) {
    return this.isFirst(delta) || this.isLast(delta);
  }

  isNotEdge(delta) {
    return !this.isEdge(delta);
  }

  isEmpty(delta) {
    return this.getStatus(delta) === 'empty';
  }

  isNotEmpty(delta) {
    return !this.isEmpty(delta);
  }

  isSelected(delta) {
    return this.getStatus(delta) === 'selected';
  }

  isNotSelected(delta) {
    return !this.isSelected(delta);
  }

  isBlocked(delta) {
    return ['blocked', undefined].includes(this.getStatus(delta));
  }

  isNotBlocked(delta) {
    return !this.isBlocked(delta);
  }
}
